import React, { useState, useEffect } from 'react';
import { useParams, useSearch } from '@tanstack/react-router';
import { cn } from '@/lib/utils';
import { Card } from 'src/components/ui/card';
import { Button } from 'src/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  SpinnerIcon,
  PencilSimpleIcon,
  ArrowLeftIcon,
  CopyIcon,
  CheckIcon,
} from '@phosphor-icons/react';
import useCompany from 'src/api/core/companies/useCompany';
import ErrorMessage from 'src/components/errorMessage/ErrorMessage';
import { AuthContext } from 'src/context/auth.context';
import { isAdmin } from 'src/utils/user';
import { useViewMode } from 'src/context/view-mode.context';
import SupplierInfo from 'src/components/company/SupplierInfo';
import { CompanyAddressesTab } from 'src/components/company/tabs/CompanyAddressesTab';
import { CompanyModulesTab } from 'src/components/company/tabs/CompanyModulesTab';
import CompanyEditForm from 'src/components/company/CompanyEditForm';
import { CompanyAliasesTab } from '@/components/company/tabs/CompanyAliasesTab';
import { CompanyContactsTab } from 'src/components/company/tabs/CompanyContactsTab';
import {
  SupplierProductsTab,
  SupplierRebateAgreementsTab,
  SupplierProductGroupsTab,
} from 'src/components/company/tabs';

// TODO: Fix shared-types package exports and use proper imports instead of hardcoded strings
// import { MODULE_TYPES, ENTITY_TYPES } from '@erp/shared-types';

// Hardcoded constants (temporary fix)
const MODULE_TYPES = {
  REBATES: 'rebates',
} as const;

interface CompanyDetailPageProps {
  module?: string; // Optional module filter to show/hide certain tabs
}

const CompanyDetailPage: React.FC<CompanyDetailPageProps> = ({ module }) => {
  const params = useParams({ strict: false }) as { id: string };
  const companyId = params.id;

  const { data, isLoading, error, refetch } = useCompany(
    companyId,
    {
      include: ['addresses', 'supplier', 'aliases', 'modules', 'contacts'],
    },
    true,
  );

  const search = useSearch({ strict: false }) as { tab?: string };
  const [activeTab, setActiveTab] = useState(() => {
    // Initialize activeTab based on query param if present
    if (search.tab === 'supplier-info') {
      return 6; // Supplier Info is typically the 7th tab (index 6) when visible
    }
    return 0;
  });
  const [isEditing, setIsEditing] = useState(false);
  const [copiedName, setCopiedName] = useState(false);
  const { currentUser } = React.useContext(AuthContext);
  const { viewMode } = useViewMode();
  const showAdminFeatures = isAdmin(currentUser, viewMode);

  const handleCopyName = () => {
    if (!data?.company?.name) return;
    navigator.clipboard.writeText(data.company.name).then(() => {
      setCopiedName(true);
      setTimeout(() => setCopiedName(false), 2000);
    });
  };

  // Get enabled modules from the company data
  const enabledModules = new Set(data?.company?.enabledModules || []);

  // Determine which tabs to show based on module filter and enabled modules
  const shouldShowRebatesTabs =
    (!module || module === MODULE_TYPES.REBATES) &&
    enabledModules.has(MODULE_TYPES.REBATES) &&
    !!data?.company?.supplier; // Only show if company has a linked supplier
  const shouldShowInventoryTabs =
    (!module || module === 'inventory') && !!data?.company?.supplier; // Only show if company has a linked supplier

  // Create tab configuration
  const tabs = [
    { label: 'Products', show: shouldShowInventoryTabs },
    { label: 'Rebate Agreements', show: shouldShowRebatesTabs },
    { label: 'Rebate Product Groups', show: shouldShowRebatesTabs },
    { label: 'Contacts', show: true },
    { label: 'Addresses', show: true },
    { label: 'Aliases', show: true },
    { label: 'Modules', show: showAdminFeatures },
    { label: 'Supplier Info', show: !!data?.company?.supplier },
    { label: 'Metadata', show: showAdminFeatures },
  ].filter((tab) => tab.show);

  // Set active tab based on query param when tabs are available
  useEffect(() => {
    if (search.tab && tabs.length > 0) {
      const tabIndex = tabs.findIndex((tab) => {
        const tabName = tab.label.toLowerCase().replace(/\s+/g, '-');
        return (
          tabName === search.tab ||
          tab.label === search.tab ||
          (search.tab === 'supplier-info' && tab.label === 'Supplier Info')
        );
      });
      if (tabIndex !== -1) {
        setActiveTab(tabIndex);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.tab]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <SpinnerIcon className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (error) {
    return <ErrorMessage error={error} />;
  }

  const company = data?.company;

  if (!company) {
    return (
      <div className="p-6">
        <h2 className="text-xl font-semibold">Company not found</h2>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-full overflow-hidden">
      {/* Main content area with sidebar */}
      <Card className="h-[80vh] shadow-lg border max-w-[calc(100vw-2rem)] p-0">
        <div className="flex h-full w-full overflow-hidden">
          {/* Left Panel - Basic Info */}
          <div className="w-[400px] min-w-[350px] max-w-[500px] border-r overflow-y-auto flex-shrink-0">
            <div className="p-0">
              <div className="flex justify-between items-center mb-6 p-6">
                <h2 className="text-xl font-medium">{company.name}</h2>
                {!isEditing && showAdminFeatures && (
                  <Button
                    size="sm"
                    onClick={() => setIsEditing(true)}
                    className="ml-4 min-w-[80px] h-8"
                  >
                    <PencilSimpleIcon className="h-3 w-3 mr-1" />
                    Edit
                  </Button>
                )}
              </div>
              {isEditing ? (
                <div className="px-6 pb-6">
                  <CompanyEditForm
                    company={company}
                    onSave={() => {
                      setIsEditing(false);
                      refetch();
                    }}
                    onCancel={() => setIsEditing(false)}
                  />
                </div>
              ) : (
                <div className="px-6">
                  <div className="mb-4">
                    <span className="text-xs font-bold text-gray-600 block">
                      Name
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm">{company.name || '-'}</span>
                      {showAdminFeatures && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6"
                                onClick={handleCopyName}
                              >
                                {copiedName ? (
                                  <CheckIcon className="h-3 w-3 text-green-600" />
                                ) : (
                                  <CopyIcon className="h-3 w-3" />
                                )}
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>
                                {copiedName ? 'Copied!' : 'Copy company name'}
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </div>
                  </div>
                  <InfoRow label="Email" value={company.email} />
                  <InfoRow
                    label="Internal Code"
                    value={
                      (company.metadata as Record<string, unknown>)?.rexel_code
                        ? String(
                            (company.metadata as Record<string, unknown>)
                              .rexel_code,
                          )
                        : undefined
                    }
                  />
                  <InfoRow
                    label="Default Currency"
                    value={company.metadata?.defaultCurrency || 'CAD'}
                  />
                  <InfoRow
                    label="Default Tax Rate"
                    value={
                      company.metadata?.defaultTaxRate
                        ? `${company.metadata.defaultTaxRate}%`
                        : undefined
                    }
                  />
                  <InfoRow
                    label="Address"
                    value={
                      company.primaryAddress
                        ? `${company.primaryAddress.addressLine1}, ${company.primaryAddress.locality}, ${company.primaryAddress.administrativeArea}, ${company.primaryAddress.postalCode}`
                        : undefined
                    }
                  />
                  {company.supplier && (
                    <>
                      <InfoRow
                        label="Linked Supplier"
                        value={company.supplier.name}
                      />
                      <InfoRow
                        label="Supplier Status"
                        value={company.supplier.status}
                      />
                    </>
                  )}
                </div>
              )}

              {company.supplier && (
                <SupplierInfo company={company} variant="sidebar" />
              )}
            </div>
          </div>

          {/* Right Panel - Sidebar and Content */}
          <div className="flex-1 flex h-full min-w-0">
            {/* Sidebar Navigation */}
            <div className="w-48 border-r overflow-y-auto h-full flex-shrink-0">
              {/* Sections Header */}
              <div className="px-4 py-4 border-b bg-gray-50">
                <span className="text-xs font-bold uppercase tracking-wider text-gray-600">
                  Sections
                </span>
              </div>

              {/* Tab Navigation */}
              {tabs.map((tab, index) => (
                <div
                  key={tab.label}
                  onClick={() => setActiveTab(index)}
                  className={cn(
                    'p-4 cursor-pointer border-b border-l-4 relative transition-colors group',
                    activeTab === index
                      ? 'bg-blue-50 border-l-blue-600 border-b-gray-100'
                      : 'bg-white border-l-transparent border-b-gray-100 hover:bg-blue-50/50 hover:border-l-blue-300',
                  )}
                >
                  <span
                    className={cn(
                      'text-sm',
                      activeTab === index
                        ? 'font-semibold text-blue-700'
                        : 'font-medium text-gray-700 group-hover:text-blue-600',
                    )}
                  >
                    {tab.label}
                  </span>
                </div>
              ))}
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-y-auto overflow-x-hidden p-0 min-w-0">
              {/* Products Tab */}
              {tabs[activeTab]?.label === 'Products' && company.supplier && (
                <SupplierProductsTab products={[]} />
              )}

              {/* Rebate Agreements Tab */}
              {tabs[activeTab]?.label === 'Rebate Agreements' &&
                company.supplier && (
                  <SupplierRebateAgreementsTab
                    supplierId={company.supplier.id}
                  />
                )}

              {/* Rebate Product Groups Tab */}
              {tabs[activeTab]?.label === 'Rebate Product Groups' &&
                company.supplier && (
                  <SupplierProductGroupsTab supplierId={company.supplier.id} />
                )}

              {/* Contacts Tab */}
              {tabs[activeTab]?.label === 'Contacts' && (
                <div className="p-6">
                  <CompanyContactsTab company={company} onRefresh={refetch} />
                </div>
              )}

              {/* Addresses Tab */}
              {tabs[activeTab]?.label === 'Addresses' && (
                <div className="p-6">
                  <CompanyAddressesTab company={company} onRefresh={refetch} />
                </div>
              )}

              {/* Aliases Tab */}
              {tabs[activeTab]?.label === 'Aliases' && (
                <div className="p-6">
                  <CompanyAliasesTab company={company} onRefresh={refetch} />
                </div>
              )}

              {/* Modules Tab */}
              {tabs[activeTab]?.label === 'Modules' && (
                <div className="p-6">
                  <CompanyModulesTab company={company} onRefresh={refetch} />
                </div>
              )}

              {/* Supplier Info Tab */}
              {tabs[activeTab]?.label === 'Supplier Info' && (
                <div className="p-6">
                  <SupplierInfo company={company} variant="tab" />
                </div>
              )}

              {/* Metadata Tab (Admin Only) */}
              {tabs[activeTab]?.label === 'Metadata' && (
                <div className="p-6">
                  <h3 className="text-lg font-medium mb-4">Metadata</h3>
                  {company.metadata ? (
                    <pre className="bg-gray-50 p-4 rounded-lg text-sm overflow-auto">
                      {JSON.stringify(company.metadata, null, 2)}
                    </pre>
                  ) : (
                    <div className="text-gray-500">No metadata found</div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

// Helper component for displaying info rows
const InfoRow: React.FC<{ label: string; value?: string | null }> = ({
  label,
  value,
}) => (
  <div className="mb-4">
    <span className="text-xs font-bold text-gray-600 block">{label}</span>
    <span className="text-sm">{value || '-'}</span>
  </div>
);

export default CompanyDetailPage;
